/** Clinical form action marks — mirrors backend CLINICAL_FORM_MARK_META. */
import {
  InvoiceIcon,
  PayrollIcon,
  PhoneIcon,
  PenIcon,
  EvaluationIcon,
  PrintIcon,
  CalendarIcon,
  ShieldCheckIcon,
  AlertCircleIcon,
  CloseIcon,
} from "../../../../Icons";

/** Canonical display + Action menu order: Billed → Paid → Faxed → Signed. */
export const CLINICAL_FORM_MARKS = [
  {
    type: "Billed",
    code: "B",
    label: "Billed",
    color: "#2563eb",
    Icon: InvoiceIcon,
  },
  {
    type: "Paid",
    code: "Paid",
    label: "Paid",
    color: "#16a34a",
    Icon: PayrollIcon,
  },
  {
    type: "Faxed",
    code: "F",
    label: "Faxed",
    color: "#4338ca",
    Icon: PhoneIcon,
  },
  {
    type: "Signed",
    code: "S",
    label: "Signed",
    color: "#0891b2",
    Icon: PenIcon,
  },
];

export const CLINICAL_FORM_MARK_ORDER = CLINICAL_FORM_MARKS.map((m) => m.type);

/** Display-only metadata for older mark records still in the DB. */
const LEGACY_MARKS = [
  {
    type: "Printed",
    code: "P",
    label: "Printed",
    color: "#2563eb",
    Icon: PrintIcon,
  },
  {
    type: "VisitPosted",
    code: "VP",
    label: "Visit Posted",
    color: "#7c3aed",
    Icon: CalendarIcon,
  },
  {
    type: "SignedAndReturned",
    code: "SR",
    label: "Signed",
    color: "#0891b2",
    Icon: PenIcon,
  },
  {
    type: "VisitAuthorized",
    code: "VA",
    label: "Visit Authorized",
    color: "#059669",
    Icon: ShieldCheckIcon,
  },
  {
    type: "VisitNotAuthorized",
    code: "VNA",
    label: "Visit Not Authorized",
    color: "#dc2626",
    Icon: AlertCircleIcon,
  },
  {
    type: "NonVisit",
    code: "NV",
    label: "Non-Visit",
    color: "#6b7280",
    Icon: CloseIcon,
  },
  {
    type: "ChartReviewed",
    code: "CR",
    label: "Chart Reviewed",
    color: "#d97706",
    Icon: EvaluationIcon,
  },
];

export const getMarkMeta = (markType) =>
  CLINICAL_FORM_MARKS.find((m) => m.type === markType) ||
  LEGACY_MARKS.find((m) => m.type === markType) || {
    type: markType,
    code: String(markType || "?").slice(0, 3),
    label: markType || "Unknown",
    color: "#64748b",
    Icon: EvaluationIcon,
  };

/**
 * Fixed slot layout so Flags align across every row:
 * Billed | Paid | Faxed | Signed (+ any legacy marks after).
 * Missing canonical marks keep an empty slot for column alignment.
 */
export const buildAlignedMarkSlots = (marks = []) => {
  const byType = new Map();
  (marks || []).forEach((m) => {
    const type = m.markType || m.type;
    if (!type || byType.has(type)) return;
    byType.set(type, m);
  });

  const slots = CLINICAL_FORM_MARK_ORDER.map((type) => ({
    type,
    mark: byType.get(type) || null,
    reserved: true,
  }));

  (marks || []).forEach((m) => {
    const type = m.markType || m.type;
    if (!type || CLINICAL_FORM_MARK_ORDER.includes(type)) return;
    if (slots.some((s) => s.type === type)) return;
    slots.push({ type, mark: m, reserved: false });
  });

  return slots;
};

/** Sort marks into canonical Billed → Paid → Faxed → Signed order. */
export const orderMarksForDisplay = (marks = []) => {
  const rank = new Map(
    CLINICAL_FORM_MARK_ORDER.map((type, index) => [type, index]),
  );
  return (marks || [])
    .slice()
    .sort((a, b) => {
      const ta = a.markType || a.type || "";
      const tb = b.markType || b.type || "";
      const ra = rank.has(ta) ? rank.get(ta) : 1000;
      const rb = rank.has(tb) ? rank.get(tb) : 1000;
      if (ra !== rb) return ra - rb;
      return String(ta).localeCompare(String(tb));
    });
};

/** Max pins shown inline next to form name before "more" popup. */
export const ATTACHMENT_PIN_PREVIEW_LIMIT = 5;

/** True if form currently has this active mark. */
export const formHasMark = (form, markType) =>
  (form?.marks || []).some((m) => (m.markType || m.type) === markType);

/**
 * For selected forms:
 * - none have mark → apply
 * - all have mark → remove
 * - mixed → both
 */
export const selectionMarkMode = (forms, selectedIds, markType) => {
  const selected = (forms || []).filter((f) => selectedIds?.has(f.id));
  if (!selected.length) return "apply";
  const withMark = selected.filter((f) => formHasMark(f, markType)).length;
  if (withMark === 0) return "apply";
  if (withMark === selected.length) return "remove";
  return "both";
};
